import {
  DocumentData,
  Query,
  QueryDocumentSnapshot,
  QuerySnapshot,
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import { isAccessStatus } from '@/utils/paid-access'
import {
  ConnectionStatus,
  FirestoreReference,
  ICustomerSubscription,
  ICustomerSubscriptionRaw,
  IGenericError,
  IUser,
} from '../types'
import { Unsubscribe } from 'firebase/auth'
import { getStudentConnectionsByEmail } from './connections'
import { IOnUpdateProps } from '@/hooks/useSubscriptionInfo'
import { generateMetadataSubscriptionAssignmentKey } from '@/utils/subscriptions'

export async function getSubscribedCustomer(
  onUpdate: (props: IOnUpdateProps) => void,
  onError: (error: unknown) => void,
  user?: IUser | null,
): Promise<Unsubscribe | undefined> {
  if (!user || !user.uid || !user.email) {
    return undefined
  }

  try {
    let subscriptionFromSponsor: ICustomerSubscription | undefined = undefined

    if (user.sponsorId) {
      subscriptionFromSponsor =
        await checkAndGetSubscriptionFromConnectedParent(
          user.uid,
          user.email,
          user.sponsorId,
        )
    }

    const subCollectionRef = query(
      collection(firebaseDb, 'customers', user.uid, 'subscriptions'),
    )

    const unsubscribe = getUnsubscribe(
      subCollectionRef,
      onUpdate,
      onError,
      subscriptionFromSponsor,
    )

    return unsubscribe
  } catch (error: unknown) {
    console.error('error on getSubscribeCustomer call', error)

    onError(error)
    return undefined
  }
}

// Whether the student ever had a Stripe trial (the Firestore mirror of their
// subscriptions keeps trial_start), so the cap moment offers "7 days free"
// only to students who can still get it (#3771). Same rule as
// create-trial-checkout, without its legacy_trial_eligible exception (the
// customer metadata is not mirrored). Unknown -> false.
export async function hasUsedTrial(uid: string): Promise<boolean> {
  try {
    const snapshot = await getDocs(
      collection(firebaseDb, 'customers', uid, 'subscriptions'),
    )

    return snapshot.docs.some((doc) => !!doc.data()?.trial_start)
  } catch (error) {
    console.warn('[CAP_MOMENT] trial check failed', error)

    return false
  }
}

export function getUnsubscribe(
  subCollectionRef: Query<DocumentData>,
  onUpdate: (props: IOnUpdateProps) => void,
  onError: (error: unknown) => void,
  subscriptionFromSponsor?: ICustomerSubscription,
): Unsubscribe {
  const unsubscribe = onSnapshot(
    subCollectionRef,
    (querySnapshot: QuerySnapshot<ICustomerSubscriptionRaw>) => {
      if (querySnapshot.empty) {
        onUpdate({ subscriptionFromSponsor })
        return
      }

      const subscriptions: ICustomerSubscription[] = []

      querySnapshot.forEach(
        (doc: QueryDocumentSnapshot<ICustomerSubscriptionRaw>) => {
          const subscriptionData: ICustomerSubscription = {
            id: doc.id,
            ...doc.data(),
          }

          const productId =
            subscriptionData.product._key.path.segments.slice(-1)[0]

          // subscriptionData.product = productId
          subscriptionData.product = {
            _key: {
              path: {
                segments: [productId],
              },
            },
          }

          // const priceData: FirestoreReference[] = subscriptionData.prices.map(
          //   (price) => {
          //     const data: FirestoreReference = {
          //       product: price._key.path.segments[1],
          //       price: price._key.path.segments[3],
          //     }

          //     return data
          //   },
          // )
          const priceData: FirestoreReference[] = subscriptionData.prices.map(
            (price) => ({
              _key: {
                path: {
                  segments: [
                    'products',
                    price._key.path.segments[1],
                    'prices',
                    price._key.path.segments[3],
                  ],
                },
              },
            }),
          )

          subscriptionData.prices = priceData

          subscriptions.push(subscriptionData)
        },
      )

      onUpdate({ subscriptions, subscriptionFromSponsor })
    },
    onError,
  )

  return unsubscribe
}

export async function checkAndGetSubscriptionFromConnectedParent(
  studentId: string,
  studentEmail: string,
  parentId: string,
): Promise<ICustomerSubscription | undefined> {
  try {
    // get student connections by email
    const studentConnections = await getStudentConnectionsByEmail(studentEmail)

    // find APPROVED connection with parent
    const connectionWithParent = studentConnections.find(
      (connection) =>
        connection.status === ConnectionStatus.Approved &&
        connection.parentId === parentId,
    )

    if (!connectionWithParent || !connectionWithParent.parentId) {
      return undefined
    }

    // get parent subscriptions
    const parentsSubscriptions: ICustomerSubscription[] =
      await getCustomerSubscriptions(connectionWithParent.parentId)

    const assignmentKey = generateMetadataSubscriptionAssignmentKey(studentId)

    // find subscription assigned to target student
    // if there are few subscriptions for one student,
    // the latest will be picked, because of parentsSubscriptions sort order (created: descending)
    const subscriptionAssignedToStudent: ICustomerSubscription | undefined =
      parentsSubscriptions.find((subscription) => {
        const assignmentId: string | undefined =
          subscription.metadata?.[assignmentKey]

        return assignmentId === studentId
      })

    return subscriptionAssignedToStudent
  } catch (error: unknown) {
    console.error(
      `Error while trying to check and get subscription from parent, studentEmail: ${studentEmail}, parentId: ${parentId} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to check and get subscription from parent`,
    )
  }
}

export async function getCustomerSubscriptions(
  customerId: string,
): Promise<ICustomerSubscription[]> {
  try {
    // orderBy is used to get subscriptions in order - latest-first. it is crucial for case,
    // when there is more then one subscription assigned to same student to show the latest one
    const subCollectionRef = query(
      collection(firebaseDb, 'customers', customerId, 'subscriptions'),
      orderBy('created', 'desc'),
    )

    const subscriptionsQuerySnapshot = (await getDocs(
      subCollectionRef,
    )) as unknown as QuerySnapshot<ICustomerSubscriptionRaw>

    const subscriptions: ICustomerSubscription[] = []

    subscriptionsQuerySnapshot.forEach((subscriptionSnapshot) => {
      const targetPlan: ICustomerSubscription = {
        id: subscriptionSnapshot.id,
        ...subscriptionSnapshot.data(),
      }

      // Include 'trialing' so a sponsoring parent's trial sub grants their
      // student perceived access during the trial window, and 'past_due' while
      // Stripe retries a failed renewal.
      if (!isAccessStatus(targetPlan.status)) {
        return
      }

      subscriptions.push(targetPlan)
    })

    return subscriptions
  } catch (error: unknown) {
    console.error(
      `Error while trying to get customers subscriptions sub collection, customerId: ${customerId}:>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get customers subscriptions sub collection`,
    )
  }
}
